import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Boxes,
  Building2,
  BookOpenCheck,
  CheckCheck,
  ChevronDown,
  Circle,
  FileBarChart,
  FolderOpen,
  HandCoins,
  Handshake,
  Landmark,
  LayoutDashboard,
  PanelLeftClose,
  PanelLeftOpen,
  Percent,
  Plug,
  ReceiptIndianRupee,
  Settings,
  Truck,
  Warehouse,
  type LucideIcon,
} from 'lucide-react';
import { NAVIGATION, type NavSection } from '@bruhat/shared';
import { cn } from '@/lib/cn';
import { useUiStore } from '@/store/ui.store';
import { useHomeLabel, useHomePath } from '@/hooks/use-home-path';
import { usePermissions } from '@/hooks/use-permissions';

/**
 * Explicit icon map. A namespace import of lucide-react would pull every icon
 * in the library into the entry chunk — roughly 800 KB for a sidebar.
 */
const NAV_ICONS: Record<string, LucideIcon> = {
  LayoutDashboard,
  BookOpenCheck,
  Building2,
  HandCoins,
  Truck,
  Boxes,
  Landmark,
  ReceiptIndianRupee,
  Percent,
  Warehouse,
  Handshake,
  FileBarChart,
  FolderOpen,
  CheckCheck,
  Settings,
  Plug,
};

function NavIcon({ name, className }: { name: string; className?: string }) {
  const Icon = NAV_ICONS[name] ?? Circle;
  return <Icon className={className} />;
}

export function Sidebar() {
  const location = useLocation();
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const toggle = useUiStore((s) => s.toggleSidebar);
  const mobileOpen = useUiStore((s) => s.sidebarMobileOpen);
  const setMobileOpen = useUiStore((s) => s.setSidebarMobileOpen);
  const { can } = usePermissions();

  const homePath = useHomePath();
  const homeLabel = useHomeLabel();

  // Only show what the signed-in role can actually reach.
  const sections = useMemo(
    () =>
      NAVIGATION.map((section) => ({
        ...section,
        /*
          The first item is "home". In Simple Mode that is the business
          overview, not the accountant's dashboard — sending someone there from
          their own navigation is how people ended up looking at debits and
          credits without having asked to.
        */
        ...(section.path === "/dashboard"
          ? { path: homePath, label: homeLabel }
          : {}),
        children: section.children?.filter((child) => !child.permission || can(child.permission)),
      })).filter(
        (section) =>
          (!section.permission || can(section.permission)) &&
          (!section.children || section.children.length > 0),
      ),
    [can, homePath, homeLabel],
  );

  const activeSection = useMemo(
    () =>
      sections.find(
        (section) =>
          section.path === location.pathname ||
          section.children?.some((child) => location.pathname.startsWith(child.path)),
      ),
    [sections, location.pathname],
  );

  const [expanded, setExpanded] = useState<string[]>(() =>
    activeSection?.label ? [activeSection.label] : [],
  );

  // Keep the group containing the current route open as the user navigates.
  useEffect(() => {
    if (activeSection?.children && !expanded.includes(activeSection.label)) {
      setExpanded((current) => [...current, activeSection.label]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection?.label]);

  // On a phone the sidebar is a sheet, so close it after navigating.
  useEffect(() => {
    setMobileOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <>
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-ink/40 backdrop-blur-[2px] lg:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex flex-col border-r border-surface-border bg-white transition-all duration-200 dark:border-night-border dark:bg-night-raised',
          'lg:static lg:translate-x-0',
          collapsed ? 'lg:w-[68px]' : 'lg:w-60',
          mobileOpen ? 'w-64 translate-x-0' : '-translate-x-full w-64',
        )}
      >
        <div className="flex h-14 shrink-0 items-center gap-2 border-b border-surface-border px-3 dark:border-night-border">
          {/* The logo means "home", and home depends on which mode you are in. */}
          <Link to={homePath} className="flex min-w-0 items-center gap-2">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-500 text-sm font-bold text-white">
              B
            </div>
            {!collapsed && (
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-ink dark:text-slate-100">Bruhat ERP</p>
                <p className="truncate text-2xs text-ink-subtle dark:text-slate-500">
                  Builder &amp; Developer
                </p>
              </div>
            )}
          </Link>
        </div>

        <nav className="flex-1 overflow-y-auto px-2 py-3">
          {sections.map((section) => (
            <SidebarSection
              key={section.label}
              section={section}
              collapsed={collapsed}
              expanded={expanded.includes(section.label)}
              onToggle={() =>
                setExpanded((current) =>
                  current.includes(section.label)
                    ? current.filter((l) => l !== section.label)
                    : [...current, section.label],
                )
              }
              currentPath={location.pathname}
            />
          ))}
        </nav>

        <button
          onClick={toggle}
          className="hidden shrink-0 items-center gap-2 border-t border-surface-border px-4 py-3 text-xs text-ink-muted hover:text-ink dark:border-night-border dark:text-slate-400 dark:hover:text-slate-200 lg:flex"
        >
          {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          {!collapsed && 'Collapse'}
        </button>
      </aside>
    </>
  );
}

interface SidebarSectionProps {
  section: NavSection;
  collapsed: boolean;
  expanded: boolean;
  onToggle: () => void;
  currentPath: string;
}

function SidebarSection({ section, collapsed, expanded, onToggle, currentPath }: SidebarSectionProps) {
  const isActive =
    section.path === currentPath ||
    section.children?.some((child) => currentPath.startsWith(child.path));

  if (!section.children) {
    return (
      <Link
        to={section.path ?? '#'}
        title={collapsed ? section.label : undefined}
        className={cn(
          'mb-0.5 flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium transition-colors',
          isActive
            ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-200'
            : 'text-ink-muted hover:bg-surface-muted hover:text-ink dark:text-slate-400 dark:hover:bg-night-sunken dark:hover:text-slate-100',
        )}
      >
        <NavIcon name={section.icon} className="h-4 w-4 shrink-0" />
        {!collapsed && <span className="truncate">{section.label}</span>}
      </Link>
    );
  }

  return (
    <div className="mb-0.5">
      <button
        onClick={onToggle}
        title={collapsed ? section.label : undefined}
        className={cn(
          'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium transition-colors',
          isActive
            ? 'text-brand-700 dark:text-brand-200'
            : 'text-ink-muted hover:bg-surface-muted hover:text-ink dark:text-slate-400 dark:hover:bg-night-sunken dark:hover:text-slate-100',
        )}
      >
        <NavIcon name={section.icon} className="h-4 w-4 shrink-0" />
        {!collapsed && (
          <>
            <span className="flex-1 truncate text-left">{section.label}</span>
            <ChevronDown
              className={cn('h-3.5 w-3.5 transition-transform', expanded && 'rotate-180')}
            />
          </>
        )}
      </button>

      {!collapsed && expanded && (
        <div className="ml-4 mt-0.5 space-y-0.5 border-l border-surface-border pl-2 dark:border-night-border">
          {section.children.map((child) => {
            const childActive = currentPath === child.path;
            return (
              <Link
                key={child.path}
                to={child.path}
                className={cn(
                  'block truncate rounded-md px-2.5 py-1.5 text-sm transition-colors',
                  childActive
                    ? 'bg-brand-50 font-medium text-brand-700 dark:bg-brand-900/30 dark:text-brand-200'
                    : 'text-ink-muted hover:bg-surface-muted hover:text-ink dark:text-slate-400 dark:hover:bg-night-sunken dark:hover:text-slate-100',
                )}
              >
                {child.label}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
